/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, IconButton, Stack, Typography } from '@mui/material';
import type { SystemIssueAlert } from '@poweralpha/common';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSystemIssueAlertsQuery } from './api';

const dismissedStorageKey = (id: string) => `system-issue-alert-dismissed:${id}`;

const isDismissed = (alert: SystemIssueAlert) =>
  localStorage.getItem(dismissedStorageKey(alert.id)) === 'true';

const formatStartedAt = (startedAt: Date) =>
  new Date(startedAt).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

interface Props {
  onHeightChange?: (height: number) => void;
}

const SystemIssueBanners = ({ onHeightChange }: Readonly<Props>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());

  const { data } = useSystemIssueAlertsQuery<Array<SystemIssueAlert>>({
    active: true,
    select: (result) => result.rows,
    meta: { suppressErrorToast: true },
  });

  const visibleAlerts = useMemo(
    () => data?.filter((alert) => !dismissedIds.has(alert.id) && !isDismissed(alert)) ?? [],
    [data, dismissedIds],
  );

  useEffect(() => {
    const element = containerRef.current;
    if (!element || visibleAlerts.length === 0) {
      onHeightChange?.(0);
      return;
    }

    const updateHeight = () => onHeightChange?.(element.getBoundingClientRect().height);
    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);

    return () => observer.disconnect();
  }, [onHeightChange, visibleAlerts.length]);

  const handleDismiss = (id: string) => {
    localStorage.setItem(dismissedStorageKey(id), 'true');
    setDismissedIds((previous) => new Set(previous).add(id));
  };

  if (visibleAlerts.length === 0) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      data-testid="system-issue-banners"
      sx={{
        px: 1,
        py: 0.5,
        bgcolor: 'background.default',
      }}
    >
      <Stack spacing={0.5}>
        {visibleAlerts.map((alert) => (
          <Alert
            key={alert.id}
            severity={alert.severity}
            sx={{ '& .MuiAlert-message': { width: '100%' } }}
            action={
              <IconButton
                aria-label="Dismiss system issue alert"
                size="small"
                onClick={() => handleDismiss(alert.id)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 0.5, sm: 2 },
                width: '100%',
              }}
            >
              <Typography sx={{ flex: 1 }}>{alert.message}</Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  fontSize: 14,
                }}
              >
                Started on {formatStartedAt(alert.started_at)}
              </Typography>
            </Box>
          </Alert>
        ))}
      </Stack>
    </Box>
  );
};

export default SystemIssueBanners;
